import { useState, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores';
interface ConfigOption {
  label: string;
  value: any;
}
interface ConfigItem {
  name: string;
  type: 'text' | 'radio' | 'checkbox' | 'select';
  default: any;
  description: string;
  options?: ConfigOption[];
}
interface DynamicControlsProps {
  config?: ConfigItem[];
}
export default function DynamicControls({ config = [] }: DynamicControlsProps) {
  // 从 store 读取和更新配置值
  const { agentConfigValues, setAgentConfigValues } = useUIStore();
  const [dropdownStates, setDropdownStates] = useState<Record<number, boolean>>(
    {}
  );
  // 计算初始值
  const initialValues = useMemo(() => {
    const values: Record<string, any> = {};
    config.forEach((item, index) => {
      const key = item.name || `field_${index}`;
      if (item.type !== 'text') {
        // 如果 store 中已有值，使用 store 的值，否则使用默认值
        values[key] = agentConfigValues[key] ?? item.default;
      }
    });
    return values;
  }, [config, agentConfigValues]);
  // 当 config 变化时，初始化缺失的字段
  useEffect(() => {
    const needsUpdate = config.some((item, index) => {
      const key = item.name || `field_${index}`;
      return item.type !== 'text' && agentConfigValues[key] === undefined;
    });
    if (needsUpdate) {
      setAgentConfigValues(initialValues);
    }
  }, [config, initialValues, agentConfigValues, setAgentConfigValues]);
  const handleRadioChange = (key: string, value: any) => {
    setAgentConfigValues({ ...agentConfigValues, [key]: value });
  };
  const handleCheckboxChange = (
    key: string,
    optionIndex: number,
    checked: boolean
  ) => {
    const currentValues = Array.isArray(agentConfigValues[key])
      ? [...agentConfigValues[key]]
      : [];
    currentValues[optionIndex] = checked;
    setAgentConfigValues({ ...agentConfigValues, [key]: currentValues });
  };
  const handleSelectChange = (key: string, value: any) => {
    setAgentConfigValues({ ...agentConfigValues, [key]: value });
  };
  const toggleDropdown = (index: number) => {
    setDropdownStates(prev => ({ ...prev, [index]: !prev[index] }));
  };
  const getSelectedLabel = (item: ConfigItem, key: string) => {
    const value = agentConfigValues[key] ?? item.default;
    const option = item.options?.find(opt => opt.value === value);
    return option?.label || 'Select...';
  };
  return (
    <div className="flex items-center gap-4 flex-wrap">
      {config.map((item, index) => {
        const key = item.name || `field_${index}`;
        switch (item.type) {
          case 'text':
            return (
              <div key={index} className="text-sm text-gray-500 italic">
                {item.name && <span className="font-medium">{item.name} </span>}
                {item.description}
              </div>
            );
          case 'radio':
            return (
              <div key={index} className="flex items-center gap-2">
                {item.name && (
                  <span className="text-sm text-gray-700 font-medium mr-1">
                    {item.name}
                  </span>
                )}
                <div className="flex items-center gap-3">
                  {item.options?.map((option, optIndex) => (
                    <label
                      key={optIndex}
                      className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={key}
                        value={option.value}
                        checked={
                          (agentConfigValues[key] ?? item.default) ===
                          option.value
                        }
                        onChange={e =>
                          handleRadioChange(
                            key,
                            e.target.type === 'radio'
                              ? option.value
                              : e.target.value
                          )
                        }
                        className="focus:ring-2"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          case 'checkbox':
            return (
              <div key={index} className="flex items-center gap-2">
                {item.name && (
                  <span className="text-sm text-gray-700 font-medium mr-1">
                    {item.name}
                  </span>
                )}
                <div className="flex items-center gap-3">
                  {item.options?.map((option, optIndex) => {
                    const currentValue = agentConfigValues[key] ?? item.default;
                    return (
                      <label
                        key={optIndex}
                        className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={
                            Array.isArray(currentValue)
                              ? currentValue[optIndex] || false
                              : false
                          }
                          onChange={e =>
                            handleCheckboxChange(
                              key,
                              optIndex,
                              e.target.checked
                            )
                          }
                          className="rounded border-gray-300 focus:ring-2"
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          case 'select':
            return (
              <div key={index} className="relative flex items-center gap-2">
                {item.name && (
                  <span className="text-sm text-gray-700 font-medium">
                    {item.name}
                  </span>
                )}
                <div className="relative">
                  <button
                    onClick={() => toggleDropdown(index)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors min-w-32"
                  >
                    <span className="flex-1 text-left">
                      {getSelectedLabel(item, key)}
                    </span>
                    <ChevronDown size={14} />
                  </button>
                  {dropdownStates[index] && (
                    <div className="absolute bottom-full mb-1 left-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-full">
                      {item.options?.map((option, optIndex) => (
                        <button
                          key={optIndex}
                          onClick={() => {
                            handleSelectChange(key, option.value);
                            toggleDropdown(index);
                          }}
                          className={cn(
                            'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-md last:rounded-b-md whitespace-nowrap',
                            (agentConfigValues[key] ?? item.default) ===
                              option.value
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-700'
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {item.description && (
                  <span className="text-xs text-gray-500 italic">
                    {item.description}
                  </span>
                )}
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
